import { inject, Injectable, effect } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { AuthService } from '@brightside/core/auth';

@Injectable({ providedIn: 'root' })
export class SentryService {
  private _authService = inject(AuthService);

  constructor() {
    // Reactively respond to auth state changes
    effect(() => {
      const isAuth = this._authService.loggedIn();

      if (isAuth) {
        this._setUserContext();
      } else {
        this._clearUserContext();
      }
    });
  }

  private async _setUserContext(): Promise<void> {
    try {
      const user = await this._authService.getUser();
      if (!user) return;

      Sentry.setUser({
        id: user.sub,
        email: user.email,
        username: user.preferred_username ?? user.name,
      });

      const groups = this._authService.getUserGroups();
      if (groups.length) {
        Sentry.setTag('user_groups', groups.join(','));
      }
    } catch (err) {
      console.warn('Failed to set Sentry user context:', err);
    }
  }

  private _clearUserContext(): void {
    Sentry.setUser(null);
  }

  captureMessage(msg: string, level: Sentry.SeverityLevel = 'info'): void {
    Sentry.captureMessage(msg, level);
  }

  captureError(err: unknown): void {
    if (err instanceof Error) {
      Sentry.captureException(err);
    } else {
      try {
        Sentry.captureException(new Error(JSON.stringify(err)));
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  setTag(key: string, value: string): void {
    Sentry.setTag(key, value);
  }

  setContext(name: string, context: Record<string, unknown>): void {
    Sentry.setContext(name, context);
  }

  captureApiError(params: {
    error: unknown;
    path: string;
    method: string;
    requestBody?: any;
    apiName?: string;
    handled?: boolean;
  }) {
    const { error, path, method, requestBody, apiName, handled = false } = params;

    if (handled) return;

    Sentry.addBreadcrumb({
      category: 'api',
      message: `API Error: ${method.toUpperCase()} ${path}`,
      level: 'error',
      data: {
        path,
        method,
        apiName,
        requestBody,
      },
    });

    const structuredError =
      error instanceof Error
        ? error
        : new Error(JSON.stringify(error, null, 2));

    Sentry.captureException(structuredError);
  };

  addBreadcrumb(params: {
    category: string;
    message: string;
    data?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
    type?: Sentry.Breadcrumb['type'];
  }): void {
    Sentry.addBreadcrumb({
      category: params.category,
      message: params.message,
      level: params.level ?? 'info',
      data: params.data,
      type: params.type ?? 'default',
      timestamp: Date.now() / 1000, // optional, Sentry auto-sets if omitted
    });
  }

}
